import {
  Barcode,
  Bot,
  Boxes,
  ChefHat,
  ClipboardCheck,
  Hammer,
  Home,
  Map as MapIcon,
  Network,
  Search,
  ShoppingCart,
  Thermometer,
  Utensils,
  WalletCards,
  type LucideIcon
} from "lucide-react";
import assistantShot from "@screenshots/assistant.png";
import choresShot from "@screenshots/chores.png";
import choresPhoneShot from "@screenshots/chores-mobile.png";
import dashboardShot from "@screenshots/dashboard.png";
import dashboardPhoneShot from "@screenshots/dashboard-mobile.png";
import financeShot from "@screenshots/finance.png";
import floorPlanShot from "@screenshots/floor-plan.png";
import foodShot from "@screenshots/groceries.png";
import foodPhoneShot from "@screenshots/groceries-mobile.png";
import foodFrozenShot from "@screenshots/features/groceries-frozen-lot.png";
import foodQuickAddShot from "@screenshots/features/groceries-quick-add-learning.png";
import inventoryShot from "@screenshots/inventory.png";
import mealsShot from "@screenshots/meal-assembler.png";
import networkShot from "@screenshots/network.png";
import projectsShot from "@screenshots/projects.png";
import recipesShot from "@screenshots/recipes.png";
import recipesMissingShot from "@screenshots/features/recipes-add-missing.png";
import searchShot from "@screenshots/search.png";
import shoppingShot from "@screenshots/shopping.png";
import shoppingCheckoffPhoneShot from "@screenshots/features/shopping-trip-checkoff-mobile.png";
import shoppingPricePhoneShot from "@screenshots/features/shopping-trip-price-history-mobile.png";

// Every string in this file is shown on the public pages, so all of it follows
// ASD-STE100 Simplified Technical English: short sentences, the active voice,
// one idea per sentence, no contractions and no marketing adjectives. The rules
// that can be checked by a machine are enforced by content.ste.test.ts — read
// that file before you add copy here.
//
// The pictures come from the committed screenshot library (docs/screenshots),
// aliased into the build as "@screenshots/…". A promoted re-capture therefore
// updates these pages with no change to this file.

export type ShotDevice = "desktop" | "phone";

export type MarketingShot = {
  /** Hashed asset URL emitted by the build. */
  src: string;
  /**
   * The intrinsic pixel size of the file. Rendered onto the <img> so the browser
   * reserves the right box before the picture arrives: without it the landing
   * page grew 482px as the shots loaded, and every section below them moved.
   *
   * The library is not one size — desktop captures are 1440x1100 or 2320x2144
   * and phone captures are 390x844 — so a single CSS aspect-ratio cannot stand
   * in for these. They are checked against the real files by content.ste.test.ts,
   * which prints the correct numbers when a re-capture changes a size.
   */
  width: number;
  height: number;
  /** What a person who cannot see the picture needs to know. */
  alt: string;
  /** One or two sentences below the picture. */
  caption: string;
  device: ShotDevice;
};

export type ModuleFunction = {
  title: string;
  text: string;
};

export type ModuleGroupId = "now" | "home" | "food" | "work" | "money";

export type MarketingModule = {
  /** Path segment below /product. */
  slug: string;
  /** The name the app uses for this module in its own menu. */
  name: string;
  group: ModuleGroupId;
  /** Where the module is in the app, for a person who is signed in. */
  appPath: string;
  icon: LucideIcon;
  /** One line for the module card. */
  summary: string;
  /** Two or three sentences at the top of the module page. */
  intro: string;
  functions: ModuleFunction[];
  shots: MarketingShot[];
};

export type ModuleGroup = {
  id: ModuleGroupId;
  name: string;
  text: string;
};

export const productName = "Home-EM";

export const site = {
  tagline: "The private app for your household.",
  heroTitle: "One place for all the records of your home.",
  heroText:
    "Home-EM keeps a record of your things, your food, your work and your money. "
    + "All the records stay in one household. Only the persons that you invite can see them.",
  signInLabel: "Sign in",
  modulesLabel: "See the modules",
  // The public demo (#840). Home-EM is not open for new households, so this —
  // not the sign-in form — is what a visitor to these pages is offered.
  demoUrl: "https://demo.home-em.com",
  demoLabel: "Try the live demo",
  demoHost: "demo.home-em.com",
  availabilityNote: "Home-EM is not open to the public at this time."
} as const;

/**
 * Page chrome: headings, button labels and the sentences that sit between the
 * sections. These live here rather than inline in the components so that one
 * test can hold EVERY public sentence to the same Simplified Technical English
 * rules — a string typed straight into the JSX would escape that check.
 */
export const ui = {
  openAppLabel: "Open the app",
  modulesNavLabel: "Modules",
  productNavLabel: "Product",
  footerNavLabel: "Footer",
  breadcrumbLabel: "Breadcrumb",
  moreModulesLabel: "More modules",
  skipLinkLabel: "Skip to the content",
  // "Before" is not the pair of "Next": the two links move along a list, they do
  // not speak about time.
  beforeLabel: "Previous",
  nextLabel: "Next",
  notFoundTitle: "This page does not exist",
  notFoundText:
    "The address that you opened is not a page of this site. "
    + "Use a link below to go to a page that is here.",
  notFoundHomeLabel: "Go to the first page",
  pillarsTitle: "What Home-EM does",
  modulesTitle: "The modules",
  modulesText: "Each module does one part of the work in a home. Select a module to read more about it.",
  modulesPageText: "Each module does one part of the work in a home. The menu of the app puts them in the same five groups.",
  pagesTitle: "Pages from the app",
  pagesText: "Each picture comes from the real app. The project makes new pictures after a change to a page.",
  demoTitle: "How to see Home-EM",
  alsoTitle: "Also in the app",
  functionsTitle: "What you can do",
  shotsHeading: "Pictures of the pages",
  moduleCtaTitle: "See this module in the demo",
  moduleCtaText: "The demo shows this module with example data. You do not need an account.",
  openModuleTitle: "Open this module in your household",
  openModuleText: "You are signed in, so this module is one step away.",
  // The public contact form (#1246). "Question" and not "message" throughout:
  // one term per concept is the STE rule these pages are held to, and the page
  // exists to answer questions.
  contactNavLabel: "Contact",
  contactTitle: "Ask a question",
  contactText:
    "Send a question about Home-EM. The question goes to the person who makes it. "
    + "Give your address if you want an answer.",
  contactNameLabel: "Your name",
  contactEmailLabel: "Your email address",
  contactMessageLabel: "Your question",
  contactSubmitLabel: "Send the question",
  contactSendingLabel: "Sending",
  contactSuccessTitle: "The question is sent",
  contactSuccessText: "Your question is with the person who makes Home-EM. An answer comes to the address that you gave.",
  contactErrorText: "The question did not send. Try again in a moment."
} as const;

export const pillars: ModuleFunction[] = [
  {
    title: "One app for the household",
    text: "Home-EM holds the records of your home in one app. You do not need a different app for each job."
  },
  {
    title: "Private to your household",
    text: "Only the persons that you invite can see your records. You control what each person can open."
  },
  {
    title: "Phone and computer",
    text: "Each page changes its shape for a small screen. You can add Home-EM to the home screen of your phone."
  },
  {
    title: "Work without a network",
    text: "The app continues to work when the phone has no network. It sends your changes when the network comes back."
  }
];

export const moduleGroups: ModuleGroup[] = [
  { id: "now", name: "Now", text: "The pages that answer the question \"what is important today?\"." },
  { id: "home", name: "Home", text: "The building and the things in it." },
  { id: "food", name: "Food", text: "The kitchen, from the shelf to the meal." },
  { id: "work", name: "Work", text: "The jobs that keep the home in good condition." },
  { id: "money", name: "Money", text: "The accounts and the costs of the household." }
];

/**
 * What replaces "how to start" while Home-EM is not open to the public (#840).
 * A page that told a visitor to make a household would send them to a door that
 * does not open; the demo is the door that does.
 */
export const demoSection = {
  text:
    "Home-EM is not open to the public at this time. You cannot make a new household now. "
    + "The demo shows the app with the example data of one household. "
    + "You do not need an account for the demo."
} as const;

export const controlSection = {
  title: "You keep control of the data",
  text:
    "You invite each person to your household. You give each person only the permissions that they need. "
    + "You can remove a person at any time. Each change keeps a record of the person who made it."
} as const;

export const alsoIncluded: ModuleFunction[] = [
  {
    title: "Scan",
    text: "Point the camera of your phone at a QR label. The app opens the record of that box or that shelf."
  },
  {
    title: "Documents",
    text: "Attach a receipt, a manual or a permit to a record. The app keeps each version of the file."
  },
  {
    title: "Settings",
    text: "Invite a person to the household. Select the modules that the person can open."
  },
  {
    title: "Release notes",
    text: "Read what changed in the app after each new version."
  }
];

export const modules: MarketingModule[] = [
  {
    slug: "dashboard",
    name: "Dashboard",
    group: "now",
    appPath: "/",
    icon: Home,
    summary: "The first page. It shows the work for today and the counts for your home.",
    intro:
      "The Dashboard is the first page after you sign in. It shows the records that need attention today. "
      + "It also shows the recent changes in the household.",
    functions: [
      {
        title: "The work for today",
        text: "The page shows the chores and the tasks that are due. You can complete one from the card."
      },
      {
        title: "Counts of your records",
        text: "Cards show the number of your things, your places and your food."
      },
      {
        title: "Food to use soon",
        text: "A card counts the food items that expire in the next days."
      },
      {
        title: "Recent activity",
        text: "A list shows the recent changes. Each line shows the time of the change."
      }
    ],
    shots: [
      {
        src: dashboardShot,
        width: 1440,
        height: 1100,
        alt: "The Home-EM Dashboard on a computer, with a list of work for today and cards of counts.",
        caption: "The Dashboard shows the work for today on the left and the counts of your records on the right.",
        device: "desktop"
      },
      {
        src: dashboardPhoneShot,
        width: 390,
        height: 844,
        alt: "The Home-EM Dashboard on a phone, with the same panels in one column.",
        caption: "On a phone the same panels are in one column.",
        device: "phone"
      }
    ]
  },
  {
    slug: "assistant",
    name: "Assistant",
    group: "now",
    appPath: "/assistant",
    icon: Bot,
    summary: "Ask a question about your home. The assistant answers from your own records.",
    intro:
      "The Assistant answers questions about your household. It reads only the records of your household. "
      + "Each answer shows the records that it used.",
    functions: [
      {
        title: "Questions in usual words",
        text: "Type a question such as \"What is on the shopping list?\". The Assistant gives an answer."
      },
      {
        title: "Answers from your data",
        text: "The Assistant reads your own records. It does not read the records of a different household."
      },
      {
        title: "A source for each answer",
        text: "The Assistant shows a chip for each record in the answer. Select a chip to open the record."
      },
      {
        title: "You approve each change",
        text: "The Assistant can propose a change to a record. You must approve the change before the app makes it."
      }
    ],
    shots: [
      {
        src: assistantShot,
        width: 2320,
        height: 2144,
        alt: "The Home-EM Assistant page with an answer and the records that the answer came from.",
        caption: "The Assistant answers a question about the household and names the records that it used.",
        device: "desktop"
      }
    ]
  },
  {
    slug: "search",
    name: "Search",
    group: "now",
    appPath: "/search",
    icon: Search,
    summary: "Find a thing, a food item, a chore or a document from one box.",
    intro:
      "Search looks in all the modules at the same time. Type some words and the app shows the matches. "
      + "Each result shows the module that it belongs to.",
    functions: [
      {
        title: "One box for all modules",
        text: "The app looks in your things, your food, your chores, your projects and your documents."
      },
      {
        title: "Filters",
        text: "Select a type of record to make the list of results shorter."
      },
      {
        title: "Direct links",
        text: "Select a result to open that record in its own module."
      },
      {
        title: "Old records",
        text: "Add the archived records and the completed records to the results when you need them."
      }
    ],
    shots: [
      {
        src: searchShot,
        width: 1440,
        height: 1100,
        alt: "The Home-EM Search page with results for one word, from several different modules.",
        caption: "One search for the word \"pantry\" finds a location, a project, a list and a chore.",
        device: "desktop"
      }
    ]
  },
  {
    slug: "things",
    name: "Things",
    group: "home",
    appPath: "/inventory",
    icon: Boxes,
    summary: "A record of the things that you keep, and the place where each thing is.",
    intro:
      "Things holds a record of your property. You put each thing in a house, a room or a box. "
      + "The app shows you the place of a thing at all times.",
    functions: [
      {
        title: "Places in levels",
        text: "Make a house, then rooms, then boxes and shelves. Put each thing in one place."
      },
      {
        title: "QR labels",
        text: "Print a label for a box. A scan of that label shows the contents of the box."
      },
      {
        title: "Photos and documents",
        text: "Attach a photo, a manual or a receipt to a thing."
      },
      {
        title: "Movements",
        text: "The app keeps a record when a thing moves to a new place."
      }
    ],
    shots: [
      {
        src: inventoryShot,
        width: 2320,
        height: 2144,
        alt: "The Home-EM Things page, with a tree of houses, rooms and storage places.",
        caption: "The Things page shows your home as a tree of houses, rooms and storage places.",
        device: "desktop"
      }
    ]
  },
  {
    slug: "home-map",
    name: "Home Map",
    group: "home",
    appPath: "/floor-plan",
    icon: MapIcon,
    summary: "Draw a plan of your home. See where each room is.",
    intro:
      "Home Map shows a plan of your home from above. You draw the walls, the rooms and the doors. "
      + "Then you connect a room on the plan to a room in the Things module.",
    functions: [
      {
        title: "Draw the rooms",
        text: "Draw each room on the plan. Give a name and a type to each room."
      },
      {
        title: "A link to your records",
        text: "Connect a room on the plan to a place in Things. The room then shows what is in it."
      },
      {
        title: "One plan for each floor",
        text: "Add a level for each floor of your home. Each level has its own rooms."
      },
      {
        title: "Revisions",
        text: "Publish a revision of the plan. The app keeps the earlier revisions."
      }
    ],
    shots: [
      {
        src: floorPlanShot,
        width: 1440,
        height: 1100,
        alt: "The Home-EM Home Map page with a drawn plan of the rooms of a house.",
        caption: "The plan of the main floor shows each room, and a room can point to a place in Things.",
        device: "desktop"
      }
    ]
  },
  {
    slug: "climate",
    name: "Climate",
    group: "home",
    appPath: "/bms",
    icon: Thermometer,
    summary: "Read the temperature and the humidity of your home from your own sensors.",
    intro:
      "Climate reads the thermostats and the sensors in your home. It shows the temperature and the humidity of each device. "
      + "You must connect a Matter controller before this module has data.",
    functions: [
      {
        title: "Your devices",
        text: "The page shows each thermostat and each sensor with its last value."
      },
      {
        title: "History",
        text: "The app keeps the values. A graph shows the change of a value with time."
      },
      {
        title: "Rules",
        text: "Make a rule for a device. The rule starts an action when a value passes a limit."
      },
      {
        title: "Alerts",
        text: "The page shows an alert when a rule or a controller needs attention."
      }
    ],
    shots: []
  },
  {
    slug: "network",
    name: "Network",
    group: "home",
    appPath: "/network",
    icon: Network,
    summary: "See the devices on your home network and the status of each one.",
    intro:
      "Network shows the devices that connect to your home network. "
      + "It shows the name, the address and the status of each device.",
    functions: [
      {
        title: "Device list",
        text: "The page shows each device with its address and the time of the last contact."
      },
      {
        title: "Segments",
        text: "Give the app the address range of your network. The app then knows where to look."
      },
      {
        title: "Services",
        text: "Add a service such as a web page on a device. The app tests the service and reports the result."
      },
      {
        title: "Trust",
        text: "Give a trust level to a device. The page shows the devices that have no trust level."
      }
    ],
    shots: [
      {
        src: networkShot,
        width: 1440,
        height: 1100,
        alt: "The Home-EM Network page with a list of the devices on the home network.",
        caption: "Each device shows its address, its type and the time of the last contact.",
        device: "desktop"
      }
    ]
  },
  {
    slug: "food",
    name: "Food",
    group: "food",
    appPath: "/groceries",
    icon: Barcode,
    summary: "The food in your kitchen, with the place and the expiry date of each item.",
    intro:
      "Food shows the items in your pantry, your fridge and your freezer. "
      + "Each item has a quantity, a place and an expiry date. The app tells you when an item becomes old.",
    functions: [
      {
        title: "Quick Add in usual words",
        text: "Type a line such as \"2 gal milk, expires Friday\". The app makes the record from that line."
      },
      {
        title: "Barcodes",
        text: "Read the barcode of a package with the camera of your phone. The app finds the food."
      },
      {
        title: "Expiry dates",
        text: "The page shows the items that expire soon. A frozen item keeps its date while it stays frozen."
      },
      {
        title: "Stock check",
        text: "The reconcile mode helps you correct the quantity and the place of each item."
      }
    ],
    shots: [
      {
        src: foodShot,
        width: 1440,
        height: 1100,
        alt: "The Home-EM Food page with the stock of the pantry, the fridge and the freezer.",
        caption: "Each line on the Food page is one lot of food, with its place and its expiry date.",
        device: "desktop"
      },
      {
        src: foodPhoneShot,
        width: 390,
        height: 844,
        alt: "The Home-EM Food page on a phone.",
        caption: "The same page on a phone is for use in the kitchen.",
        device: "phone"
      },
      {
        src: foodQuickAddShot,
        width: 1440,
        height: 1100,
        alt: "The Quick Add box of the Food page, after one line of text made a new record.",
        caption: "One Quick Add line put the food in its usual place and recorded its shelf life.",
        device: "desktop"
      },
      {
        src: foodFrozenShot,
        width: 1440,
        height: 1100,
        alt: "A frozen lot on the Food page, with its quantity, its brand and its price.",
        caption: "A frozen lot keeps its quantity, its brand, its price and the date that it went in.",
        device: "desktop"
      }
    ]
  },
  {
    slug: "recipes",
    name: "Recipes",
    group: "food",
    appPath: "/recipes",
    icon: ChefHat,
    summary: "Your recipes, with a check of the ingredients that the kitchen has.",
    intro:
      "Recipes keeps the ingredients and the steps of each recipe. The app compares the ingredients with your food records. "
      + "A badge shows the ingredients that you do not have.",
    functions: [
      {
        title: "Recipe records",
        text: "Keep the ingredients, the steps, the times and a photo for each recipe."
      },
      {
        title: "A scale for the servings",
        text: "Change the number of servings. The app changes the quantity of each ingredient."
      },
      {
        title: "Add what you do not have",
        text: "One button puts the ingredients that the kitchen does not have on a shopping list."
      },
      {
        title: "Cook mode",
        text: "Cook mode shows the steps one at a time. A timer counts the minutes of a step."
      }
    ],
    shots: [
      {
        src: recipesShot,
        width: 2320,
        height: 2144,
        alt: "The Home-EM Recipes page with a grid of recipe cards.",
        caption: "Each card on the Recipes page shows how much of the recipe the kitchen can supply.",
        device: "desktop"
      },
      {
        src: recipesMissingShot,
        width: 1440,
        height: 1100,
        alt: "A recipe page with a button that adds the ingredients that are not in the kitchen.",
        caption: "The button and the list agree. Only the ingredients that you do not have go to the list.",
        device: "desktop"
      }
    ]
  },
  {
    slug: "meals",
    name: "Meals",
    group: "food",
    appPath: "/meal-assembler",
    icon: Utensils,
    summary: "A plan of the meals for the week, from the food that you have.",
    intro:
      "Meals shows a plan for each day of the week. Put a recipe on a day and a mealtime. "
      + "The page also shows the meals that your kitchen can make tonight.",
    functions: [
      {
        title: "A plan by date",
        text: "Put a recipe on a date and a mealtime. Give the number of servings."
      },
      {
        title: "Meals for tonight",
        text: "The page shows the meals for which you have all the ingredients."
      },
      {
        title: "Food that expires soon",
        text: "The page gives a higher position to a meal that uses food with a near date."
      },
      {
        title: "A note for a free night",
        text: "Plan a night with a note only, when there is no recipe for it."
      }
    ],
    shots: [
      {
        src: mealsShot,
        width: 1440,
        height: 1100,
        alt: "The Home-EM Meals page with a plan of the week and a form to add a meal.",
        caption: "Each day of the meal plan shows the meals that you planned for it.",
        device: "desktop"
      }
    ]
  },
  {
    slug: "shopping",
    name: "Shopping",
    group: "food",
    appPath: "/shopping",
    icon: ShoppingCart,
    summary: "Shopping lists and store trips that keep a record of the prices.",
    intro:
      "Shopping keeps your lists and your store trips. In the store you mark each item on your phone. "
      + "The app keeps a record of the price that you paid for each item.",
    functions: [
      {
        title: "Lists",
        text: "Make a list for each store. Add an item by name, or add it from a recipe."
      },
      {
        title: "Trips",
        text: "Start a trip when you are in the store. Mark each item that goes in the cart."
      },
      {
        title: "Price history",
        text: "The app shows the usual price of an item. It tells you when the new price is higher."
      },
      {
        title: "Food from the trip",
        text: "The items of a completed trip become food records in your kitchen."
      }
    ],
    shots: [
      {
        src: shoppingShot,
        width: 1440,
        height: 1100,
        alt: "The Home-EM Shopping page with the lists of the household and the recent trips.",
        caption: "The Shopping page keeps the lists on one side and the store trips on the other.",
        device: "desktop"
      },
      {
        src: shoppingCheckoffPhoneShot,
        width: 390,
        height: 844,
        alt: "A store trip on a phone, with items marked as in the cart.",
        caption: "In the store one tap marks an item, and the total at the bottom follows each tap.",
        device: "phone"
      },
      {
        src: shoppingPricePhoneShot,
        width: 390,
        height: 844,
        alt: "A store trip on a phone that shows the usual price of an item below the price box.",
        caption: "The usual price is at the point of the decision, with a flag when the price is higher.",
        device: "phone"
      }
    ]
  },
  {
    slug: "chores",
    name: "Chores",
    group: "work",
    appPath: "/chores",
    icon: ClipboardCheck,
    summary: "The shared list of the work in the house.",
    intro:
      "Chores shows the work of the household in one list. A chore can occur one time, or it can repeat on a schedule. "
      + "Each person sees the work that is due today.",
    functions: [
      {
        title: "Chores that repeat",
        text: "Give a schedule to a chore. The app makes the next chore after you complete this one."
      },
      {
        title: "One person for each chore",
        text: "Give a chore to a person in the household. That person then sees it on the Dashboard."
      },
      {
        title: "The work for today",
        text: "The page puts the chores that are due today at the top of the list."
      },
      {
        title: "Snooze",
        text: "Move a chore to a later date when today is not possible."
      }
    ],
    shots: [
      {
        src: choresShot,
        width: 2320,
        height: 2144,
        alt: "The Home-EM Chores page with the household work in groups by date.",
        caption: "The Chores page puts the work in groups, and the work for today is at the top.",
        device: "desktop"
      },
      {
        src: choresPhoneShot,
        width: 390,
        height: 844,
        alt: "The Home-EM Chores page on a phone.",
        caption: "The same list on a phone is for a check between two jobs.",
        device: "phone"
      }
    ]
  },
  {
    slug: "projects",
    name: "Projects",
    group: "work",
    appPath: "/projects",
    icon: Hammer,
    summary: "Large work in the home, with a budget, materials, permits and documents.",
    intro:
      "Projects holds the large work in your home. Each project has tasks, materials, costs and documents. "
      + "The page compares the budget with the money that you paid.",
    functions: [
      {
        title: "Tasks",
        text: "Divide a project into tasks. Give each task a date and a person."
      },
      {
        title: "Budget and costs",
        text: "Put a budget on the project. The page shows the costs against that budget."
      },
      {
        title: "Materials",
        text: "Make a list of the materials. Send that list to the Shopping module."
      },
      {
        title: "Permits and documents",
        text: "Keep the permits, the quotations and the receipts with the project."
      }
    ],
    shots: [
      {
        src: projectsShot,
        width: 2320,
        height: 2144,
        alt: "The Home-EM Projects page with the tasks, the budget and the documents of a project.",
        caption: "One project page holds the tasks, the costs, the materials and the documents.",
        device: "desktop"
      }
    ]
  },
  {
    slug: "finance",
    name: "Finance",
    group: "money",
    appPath: "/finance",
    icon: WalletCards,
    summary: "The accounts, the budgets and the bills of the household.",
    intro:
      "Finance shows your household accounts and their balances. You put each transaction in a category. "
      + "The page shows the money in, the money out and the bills that come next.",
    functions: [
      {
        title: "Accounts and balances",
        text: "Add an account and its balance. The page adds the accounts into one total."
      },
      {
        title: "Transactions",
        text: "Put each transaction in a category. The page shows the money in and the money out for each month."
      },
      {
        title: "Budgets",
        text: "Make a monthly budget for a category. The page shows the part of the budget that you used."
      },
      {
        title: "Bills that repeat",
        text: "Add a bill and its schedule. The page shows the bills that come next."
      }
    ],
    shots: [
      {
        src: financeShot,
        width: 1440,
        height: 1100,
        alt: "The Home-EM Finance page with the net worth, the cash flow and the budgets of the household.",
        caption: "The cards on the Finance page show the balance, the money in and the money out.",
        device: "desktop"
      }
    ]
  }
];

export const modulesBySlug = new Map(modules.map((item) => [item.slug, item]));

export function modulesOfGroup(group: ModuleGroupId): MarketingModule[] {
  return modules.filter((item) => item.group === group);
}

/** The picture at the top of the landing page. */
export const heroShot: MarketingShot = {
  src: dashboardShot,
  width: 1440,
  height: 1100,
  alt: "The Home-EM Dashboard on a computer, with the work for today and the counts of the home.",
  caption: "The Dashboard shows the work for today, the counts of your records and the recent changes.",
  device: "desktop"
};

/** The pages the landing page shows below the module list, in this order. */
export const landingShots: MarketingShot[] = [
  {
    src: foodShot,
    width: 1440,
    height: 1100,
    alt: "The Home-EM Food page on a computer.",
    caption: "The Food page shows each lot in the pantry, the fridge and the freezer, with its date.",
    device: "desktop"
  },
  {
    src: shoppingShot,
    width: 1440,
    height: 1100,
    alt: "The Home-EM Shopping page on a computer.",
    caption: "The Shopping page holds the lists of the household and the trips that record the prices.",
    device: "desktop"
  },
  {
    src: financeShot,
    width: 1440,
    height: 1100,
    alt: "The Home-EM Finance page on a computer.",
    caption: "The Finance page shows the balance of each account, the money in and the money out.",
    device: "desktop"
  }
];
